import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-2 border-gray-200 shadow-xl" data-testid="forgot-password-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img
              src="https://customer-assets.emergentagent.com/job_waste-less-hub-1/artifacts/918kjlll_image.png"
              alt="Repair Cafe Logo"
              className="w-20 h-20 rounded-full"
            />
          </div>
          <CardTitle className="text-3xl text-[#193460]">Forgot Password</CardTitle>
          <CardDescription className="text-base">
            {sent
              ? 'Check your inbox for a reset link.'
              : 'Enter your email to receive a password reset link.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="text-center space-y-4 py-4">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
              <p className="text-gray-700">
                If an account exists for <strong>{email}</strong>, you'll receive an email with
                instructions to reset your password.
              </p>
              <p className="text-sm text-gray-500">
                The link will expire in 60 minutes. Don't forget to check your spam folder.
              </p>
              <Link to="/admin/login">
                <Button
                  variant="outline"
                  className="border-2 border-[#193460] text-[#193460] hover:bg-[#193460] hover:text-white"
                  data-testid="back-to-login-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-base font-semibold text-[#193460] mb-2 flex items-center">
                  <Mail className="w-4 h-4 mr-2" />
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className="h-12 border-2 border-gray-300 focus:border-[#E87B6A]"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                size="lg"
                data-testid="forgot-submit-btn"
                className="w-full bg-[#E87B6A] hover:bg-[#d66a59] text-white font-semibold h-14 text-lg shadow-lg"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
              <div className="text-center border-t pt-4">
                <Link
                  to="/admin/login"
                  className="text-sm text-[#193460] hover:text-[#E87B6A] inline-flex items-center"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to Login
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
